<!--
    HOC (Higher-Order Component) that dispatches to the correct tile layout
    based on the `tileLayout` prop. Add new layout variants here.
-->
<script lang="ts">
    import type { Variation } from '@shared/types/types.ts';
    import type { ImageAspectRatio } from '@tile/components/product-image/types.ts';
    import { injectWiring } from '@tilelist/application/wiring/TileListWiringContext.svelte.ts';
    import type { TileLayout } from '@tilelist/domain/composition/BracketLayout.ts';
    import type { ExperimentGroups } from '@tilelist/domain/experiments/Experiments.ts';
    import { isCutoutAllowedForPbk, isCutoutTreatment } from '@tilelist/domain/feature-visibility/CutoutImage.ts';
    import DefaultLayout from '@tilelist/layouts/default-layout/DefaultLayout.svelte';
    import { createDefaultLayout } from '@tilelist/layouts/default-layout/DefaultLayout.ts';
    import DefaultLayoutE3867 from '@tilelist/layouts/experiments/e3867/default-layout/DefaultLayout.svelte';
    import { createDefaultLayout as createDefaultLayoutE3867 } from '@tilelist/layouts/experiments/e3867/default-layout/DefaultLayout.ts';
    import FashionLayoutE3867 from '@tilelist/layouts/experiments/e3867/fashion-layout/FashionLayout.svelte';
    import FashionLayout from '@tilelist/layouts/fashion-layout/FashionLayout.svelte';
    import { createFashionLayout } from '@tilelist/layouts/fashion-layout/FashionLayout.ts';
    import type { LayoutImageIntents } from '@tilelist/layouts/mapping/LayoutImages.ts';
    import type { TileLayoutProps } from '@tilelist/layouts/shared/TileLayoutProps.ts';

    type Props = TileLayoutProps & {
        tileLayout: TileLayout;
        variation: Variation;
        aspectRatio: ImageAspectRatio;
        showTopInfos: boolean;
        showInstallments: boolean;
    };

    let { tileLayout, variation, aspectRatio, showTopInfos, showInstallments, ...coreProps }: Props = $props();

    const onex = $derived(injectWiring().onex);
    const multipleImagesMapping: Record<ExperimentGroups['ADDITIONAL_IMAGES'], boolean> = {
        E3891A: false,
        E3891B: true,
    };
    const multipleImagesActive = $derived(onex.matchGroup('ADDITIONAL_IMAGES', multipleImagesMapping));
    const cleanTileActive = $derived(onex.isExperimentGroup('CLEAN_TILE_V2', 'E3867B'));
    const cutoutActive = $derived(
        isCutoutTreatment(onex) &&
            isCutoutAllowedForPbk(variation.pbk) &&
            'images' in variation &&
            !!variation.cutout?.image?.id,
    );
    const intents: LayoutImageIntents = $derived({ cutoutActive, multipleImagesActive });
</script>

{#if tileLayout === 'fashion' && cleanTileActive}
    <FashionLayoutE3867 {...coreProps} layout={createFashionLayout(variation)} {aspectRatio} />
{:else if tileLayout === 'fashion'}
    <FashionLayout {...coreProps} layout={createFashionLayout(variation)} {aspectRatio} />
{:else if cleanTileActive}
    <DefaultLayoutE3867
        {...coreProps}
        layout={createDefaultLayoutE3867(variation, showTopInfos, intents)}
        {aspectRatio}
        {showInstallments}
    />
{:else}
    <DefaultLayout
        {...coreProps}
        layout={createDefaultLayout(variation, showTopInfos, intents)}
        {aspectRatio}
        {showInstallments}
    />
{/if}
